import React, { useCallback, useEffect, useState } from "react";
import { ScrollText, FileDown, ShieldCheck, ChevronDown, ChevronRight, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const clauseCls = (s) => s === "strong" ? "text-emerald-700 bg-emerald-50"
  : s === "developing" ? "text-amber-700 bg-amber-50"
  : s === "weak" ? "text-orange-700 bg-orange-50"
  : s === "gap" ? "text-red-700 bg-red-50" : "text-zinc-600 bg-zinc-50";
const secCls = (s) => s === "met" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : s === "partial" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-red-700 bg-red-50 border-red-200";

const govCls = (tone) => tone === "verified" ? "text-emerald-700 bg-emerald-50 border-emerald-200"
  : tone === "review" ? "text-amber-700 bg-amber-50 border-amber-200" : "text-orange-700 bg-orange-50 border-orange-200";
function GovChip({ governance }) {
  if (!governance?.badge) return null;
  return (
    <span data-testid={`gov-chip-${governance.source_id}`} title={governance.provenance}
      className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border font-semibold ${govCls(governance.badge.tone)}`}>
      <ShieldCheck size={11} /> {governance.badge.label}
    </span>
  );
}

function SignoffBar({ signoff, onApprove, onClear, testid }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const approve = async () => { setBusy(true); await onApprove(note); setBusy(false); setOpen(false); setNote(""); };
  if (signoff) {
    return (
      <div data-testid={`${testid}-approved`} className="flex items-start gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1.5 flex-wrap max-w-full">
        <CheckCircle2 size={13} className="mt-0.5 shrink-0" />
        <span className="min-w-0 break-words">Reviewed &amp; approved by <b>{signoff.reviewed_by_name || signoff.reviewed_by}</b> · {(signoff.reviewed_at || "").slice(0, 10)}{signoff.note ? ` — ${signoff.note}` : ""}</span>
        <button data-testid={`${testid}-clear`} onClick={onClear} className="ml-1 text-zinc-400 hover:text-red-600 shrink-0"><X size={13} /></button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {open ? (
        <div className="flex items-center gap-1.5">
          <input data-testid={`${testid}-note`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reviewer note (optional)"
            className="text-[12px] border border-zinc-300 rounded px-2 py-1 w-52" />
          <button data-testid={`${testid}-confirm`} disabled={busy} onClick={approve}
            className="text-[12px] px-2.5 py-1 rounded bg-[var(--e360-primary)] text-white disabled:opacity-50">Confirm approval</button>
          <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-600"><X size={14} /></button>
        </div>
      ) : (
        <button data-testid={`${testid}-approve`} onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1 text-[12px] text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2.5 py-1 rounded hover:bg-[var(--e360-primary)]/5">
          <ShieldCheck size={13} /> Mark reviewed &amp; approved</button>
      )}
    </div>
  );
}

function StandardCard({ s }) {
  const [expanded, setExpanded] = useState(false);
  const hasItems = s.granular_total > 0;
  return (
    <div data-testid={`std-${s.id}`} className="border border-zinc-200 rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
        <button onClick={() => hasItems && setExpanded((v) => !v)} className="flex items-center gap-1 text-[13px] font-semibold text-zinc-800 text-left">
          {hasItems && (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />)}
          {s.name}
        </button>
        <span data-testid={`std-coverage-${s.id}`} className="text-[12px] text-[var(--e360-primary)] font-semibold">
          {s.coverage_pct}% · {s.mapped_clauses}/{s.total_clauses} areas
          {hasItems ? <span className="text-zinc-400 font-normal"> · {s.granular_mapped}/{s.granular_total} controls</span> : null}
        </span>
      </div>
      <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden mb-2"><div className="h-full bg-[var(--e360-primary)]" style={{ width: `${s.coverage_pct}%` }} /></div>
      <div className="flex flex-wrap gap-1.5">
        {s.clauses.map((c) => (
          <span key={c.ref} title={(c.mapped_areas || []).map((a) => a.name).join(", ") || "No assessed area maps here"}
            className={`text-[11px] px-2 py-0.5 rounded ${clauseCls(c.status)}`}>
            {c.ref}: {c.title} · {c.items_total ? `${c.items_mapped}/${c.items_total} · ` : ""}{c.status}
          </span>
        ))}
      </div>
      {expanded && (
        <div className="mt-3 space-y-2" data-testid={`std-items-${s.id}`}>
          {s.clauses.filter((c) => (c.items || []).length).map((c) => (
            <div key={c.ref}>
              <div className="text-[11px] font-semibold text-zinc-500 mb-1">{c.ref} · {c.title}</div>
              <div className="flex flex-wrap gap-1">
                {c.items.map((it) => (
                  <span key={it.ref} title={(it.mapped_areas || []).map((a) => a.name).join(", ") || "Not evidenced by an assessed area"}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${clauseCls(it.status)}`}>{it.ref} {it.title}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StandardsMap({ clientId }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => { api.get(`/admin/enterprise360/clients/${clientId}/standards-map`).then(({ data }) => setData(data)).catch(() => {}); }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const approve = async (note) => { await api.post(`/admin/enterprise360/clients/${clientId}/standards-map/signoff`, { note }); load(); };
  const clear = async () => { await api.delete(`/admin/enterprise360/clients/${clientId}/standards-map/signoff`); load(); };
  if (!data) return <div className="text-sm text-zinc-400 py-4">Loading standards map…</div>;
  return (
    <div data-testid="standards-map">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="text-[12px] text-zinc-500 flex items-center gap-2 flex-wrap">
          Average coverage across {data.summary.standards} standards: <b>{data.summary.avg_coverage_pct}%</b>.
          <GovChip governance={data.governance} />
        </div>
        <SignoffBar signoff={data.signoff} onApprove={approve} onClear={clear} testid="std-signoff" />
      </div>
      <div className="text-[11px] text-amber-600 mb-3">{data.provenance}</div>
      <div className="space-y-3">
        {data.standards.map((s) => <StandardCard key={s.id} s={s} />)}
      </div>
    </div>
  );
}

function RegulatorPacks({ clientId, focusRegime }) {
  const [regimes, setRegimes] = useState([]);
  const [sel, setSel] = useState(null);
  const [pack, setPack] = useState(null);
  const [coverage, setCoverage] = useState(null);
  useEffect(() => { api.get("/admin/enterprise360/regulator-packs").then(({ data }) => setRegimes(data.regimes)).catch(() => {}); }, []);
  const loadCoverage = useCallback(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/regulator-coverage`).then(({ data }) => setCoverage(data)).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadCoverage(); }, [loadCoverage]);
  const load = useCallback((id) => {
    setSel(id); setPack(null);
    api.get(`/admin/enterprise360/clients/${clientId}/regulator-pack/${id}`).then(({ data }) => setPack(data)).catch(() => {});
  }, [clientId]);
  // Deep-link from the portfolio heatmap: open the requested regime pack on mount.
  useEffect(() => { if (focusRegime) load(focusRegime); }, [focusRegime, load]);
  const approve = async (note) => { await api.post(`/admin/enterprise360/clients/${clientId}/regulator-pack/${sel}/signoff`, { note }); load(sel); loadCoverage(); };
  const clear = async () => { await api.delete(`/admin/enterprise360/clients/${clientId}/regulator-pack/${sel}/signoff`); load(sel); loadCoverage(); };

  const downloadPdf = useCallback(async () => {
    if (!pack) return;
    const base = `/admin/enterprise360/clients/${clientId}/regulator-pack/${pack.id}/pdf`;
    const verified = pack?.governance?.badge?.verified;
    const save = (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Regulator_Pack_${pack.id}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    };
    try {
      if (!verified) {
        const ok = window.confirm("This regulator pack's content is not Ekselens-verified.\n\nDownload it marked \u201cFOR GUIDANCE ONLY \u2014 not Ekselens-verified\u201d? An owner can verify it first in Curation \u2192 Authoritative content governance.");
        if (!ok) return;
        const res = await api.get(`${base}?acknowledge=true`, { responseType: "blob" });
        save(res.data);
        toast.info("Downloaded — watermarked as for-guidance-only (unverified).");
      } else {
        const res = await api.get(base, { responseType: "blob" });
        save(res.data);
      }
    } catch (e) {
      toast.error("Could not download the regulator pack");
    }
  }, [clientId, pack]);

  return (
    <div data-testid="regulator-packs" className="mt-6">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-2">Regulator-ready packs</div>

      {coverage && coverage.regimes?.length > 0 && (
        <div data-testid="regulatory-coverage-matrix" className="mb-4 border border-zinc-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="text-[12px] text-zinc-600">
              <b>Regulatory coverage</b> — {coverage.regimes.length} regimes · avg readiness{" "}
              <b className="text-[var(--e360-primary)]">{coverage.average_pct}%</b>
              {coverage.signed_off_count > 0 && <> · {coverage.signed_off_count} signed off</>}
            </div>
          </div>
          {coverage.signed_off_count > 0 && (
            <div data-testid="reg-kit-hint" className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mb-2">
              Signed-off regimes ship inside the client Reinvention Kit{coverage.verified ? "." : " — watermarked \u201Cfor guidance only\u201D until the regulator content is Ekselens-verified in Curation."}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {coverage.regimes.map((r) => {
              const rag = r.readiness_pct >= 70 ? "emerald" : r.readiness_pct >= 45 ? "amber" : "red";
              const bar = rag === "emerald" ? "bg-emerald-500" : rag === "amber" ? "bg-amber-500" : "bg-red-500";
              const txt = rag === "emerald" ? "text-emerald-700" : rag === "amber" ? "text-amber-700" : "text-red-700";
              return (
                <button key={r.id} data-testid={`coverage-tile-${r.id}`} onClick={() => load(r.id)}
                  className={`text-left border rounded-lg p-2.5 transition-colors ${sel === r.id ? "border-[var(--e360-primary)] bg-[var(--e360-primary)]/5" : "border-zinc-200 hover:bg-zinc-50"}`}>
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[11px] font-semibold text-zinc-700 leading-tight">{r.short}</span>
                    {r.signed_off && <CheckCircle2 size={13} className="text-emerald-600 shrink-0" title="Signed off — included in the client Kit" />}
                  </div>
                  <div className={`text-[20px] font-bold ${txt} mt-1`}>{r.readiness_pct}%</div>
                  <div className="h-1.5 rounded-full bg-zinc-100 mt-1 overflow-hidden">
                    <div className={`h-full ${bar}`} style={{ width: `${r.readiness_pct}%` }} />
                  </div>
                  <div className="text-[10px] text-zinc-400 mt-1">{r.counts.met}✓ · {r.counts.partial}~ · {r.counts.gap}✗</div>
                  {r.signed_off && (
                    <div data-testid={`coverage-in-kit-${r.id}`} className="text-[10px] font-semibold text-emerald-700 mt-1">
                      ✓ In client Kit
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-2 flex-wrap mb-3">
        {regimes.map((r) => (
          <button key={r.id} data-testid={`regime-btn-${r.id}`} onClick={() => load(r.id)}
            className={`text-[12px] px-3 py-1.5 rounded border ${sel === r.id ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "border-zinc-300 text-zinc-600 hover:bg-zinc-50"}`}>{r.name.split(" — ")[0]}</button>
        ))}
      </div>
      {pack && (
        <div className="border border-zinc-200 rounded-lg p-3">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <span className="text-[13px] font-semibold">{pack.name}</span>
            <div className="flex items-center gap-3">
              <GovChip governance={pack.governance} />
              <span data-testid="reg-readiness" className="text-[12px] text-[var(--e360-primary)] font-semibold">Readiness {pack.readiness_pct}%</span>
              <button data-testid={`reg-download-${pack.id}`} onClick={downloadPdf}
                className="inline-flex items-center gap-1 text-[12px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2.5 py-1 rounded hover:bg-[var(--e360-primary)]/5"><FileDown size={12} /> PDF</button>
            </div>
          </div>
          <div className="mb-2"><SignoffBar signoff={pack.signoff} onApprove={approve} onClear={clear} testid="reg-signoff" /></div>
          <div className="text-[11px] text-amber-600 mb-2">{pack.provenance}</div>
          <div className="divide-y divide-zinc-100">
            {pack.sections.map((s) => (
              <div key={s.ref} data-testid={`reg-section-${s.ref}`} className="py-2 flex items-start justify-between gap-2 flex-wrap">
                <div className="min-w-[220px]">
                  <div className="text-[13px] text-zinc-800">{s.ref} · {s.title}</div>
                  <div className="text-[11px] text-zinc-400">Evidence: {(s.evidence_refs || []).join(", ") || "—"} ({s.count} item{s.count === 1 ? "" : "s"})</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold ${secCls(s.status)}`}>{s.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function StandardsRegulatory({ clientId, focusRegime }) {
  return (
    <div data-testid="standards-regulatory">
      <p className="text-[12px] text-zinc-500 max-w-2xl mb-4"><ScrollText size={12} className="inline mr-1" /> Map assessed capabilities to recognised standards (ISO/IEC 27001 to control level, COBIT to its 40 objectives) and generate submission-shaped regulator packs from the live evidence. <span className="text-amber-600">Clause/requirement references are from the published standards &amp; regulations; the mapping is automated for guidance — mark it reviewed once your compliance function has verified it. Not certification or legal advice.</span></p>
      <StandardsMap clientId={clientId} />
      <RegulatorPacks clientId={clientId} focusRegime={focusRegime} />
    </div>
  );
}
